import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, CornerDownRightIcon } from 'lucide-react';
import type { PeerOption } from '../types/pulse';
interface QuestionScreenProps {
  eyebrow: string;
  question: string;
  options: PeerOption[];
  accent: string;
  initialSelected?: string | null;
  contextChip?: string;
  onAnswer: (optionId: string) => void;
  onSkip?: () => void;
}
const LETTERS = ['A', 'B', 'C', 'D'];
export function QuestionScreen({
  eyebrow,
  question,
  options,
  accent,
  initialSelected = null,
  contextChip,
  onAnswer,
  onSkip
}: QuestionScreenProps) {
  const [selected, setSelected] = useState<string | null>(initialSelected);
  const [locked, setLocked] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);
  const handleSelect = (id: string) => {
    if (locked) return;
    setSelected(id);
    setLocked(true);
    timer.current = window.setTimeout(() => onAnswer(id), 520);
  };
  return <div className="relative flex h-full flex-col overflow-y-auto bg-mist-50 px-6 pb-6 pt-24 no-scrollbar md:pt-28" data-id="element-493" data-mp="1ch2d39sqgpz">
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 top-6 h-72 w-72 rounded-full opacity-[0.12] blur-3xl" style={{
      backgroundColor: accent
    }} data-id="element-494" data-mp="3jj3s0qsgbpxz" />

      <div className="relative" data-id="element-495" data-mp="g1d8uvw7floh">
        {contextChip && <motion.p initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-4 inline-flex max-w-full items-center gap-1.5 rounded-full border border-navy-900/10 bg-white px-3 py-1 text-[11px] text-slate-600" data-id="element-496" data-mp="k373onjpxvgq">
            <CornerDownRightIcon className="h-3 w-3 shrink-0" style={{
          color: accent
        }} data-id="element-497" data-mp="b923hdip37oj" />
            <span className="truncate" data-id="element-498" data-mp="yz69frjcvjip">{contextChip}</span>
          </motion.p>}
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{
        color: accent
      }} data-id="element-499" data-mp="13rzbg7nfwdr3">
          {eyebrow}
        </p>
        <motion.h2 initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.05
      }} className="mt-3 font-serif text-[28px] font-normal leading-[1.15] tracking-tight text-navy-900" data-id="element-500" data-mp="1dkttzdth0q23">
          {question}
        </motion.h2>
      </div>

      <ul className="relative mt-auto space-y-3 pt-8" role="list" data-id="element-501" data-mp="uotznwd0tafc">
        {options.map((o, i) => {
        const isSelected = selected === o.id;
        const dimmed = locked && !isSelected;
        return <motion.li key={o.id} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: dimmed ? 0.4 : 1,
          y: 0,
          scale: isSelected && locked ? 1.02 : 1
        }} transition={{
          duration: 0.4,
          delay: locked ? 0 : 0.15 + i * 0.06
        }} data-id="element-502" data-mp="kzk8qjtwxxst" data-mpi={i}>
              <button type="button" onClick={() => handleSelect(o.id)} aria-pressed={isSelected} disabled={locked} className="flex min-h-[68px] w-full items-center gap-4 rounded-2xl border bg-white px-4 py-3.5 text-left shadow-[0_1px_2px_rgba(15,36,66,0.05)] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-900/40 enabled:hover:shadow-md" style={{
            borderColor: isSelected ? accent : 'rgba(15,36,66,0.1)',
            backgroundColor: isSelected ? `${accent}12` : '#FFFFFF'
          }} data-id="element-503" data-mp="6yhkq1jcydf9">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-[12px] font-semibold" style={{
              borderColor: isSelected ? accent : 'rgba(15,36,66,0.15)',
              backgroundColor: isSelected ? accent : 'transparent',
              color: isSelected ? '#FFFFFF' : '#4A6489'
            }} data-id="element-504" data-mp="23uu97ymr9x0s">
                  {isSelected ? <CheckIcon className="h-4 w-4" data-id="element-505" data-mp="3mv3d8u530mn1" /> : LETTERS[i]}
                </span>
                <span className="text-[15px] font-medium leading-snug text-navy-900" data-id="element-506" data-mp="3iut4j9pwni4h">{o.label}</span>
              </button>
            </motion.li>;
      })}
      </ul>

      {onSkip && <button type="button" onClick={onSkip} disabled={locked} className="relative mx-auto mt-5 rounded-full px-4 py-2 text-[13px] text-slate-500 transition hover:text-navy-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-900/30" data-id="element-507" data-mp="3rufv10h3j3ir">
          Skip — still show me how peers compare
        </button>}
    </div>;
}